// UUID v7 (RFC 9562): 48-bit Unix ms timestamp, then random bits. Time-ordered, so ids sort by
// creation and index well. Generated on clients too (offline-created rows, technical design §6.1).

const HEX: string[] = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

let lastMs = -1;
let counter = 0; // 12-bit counter in rand_a keeps ids monotonic within one millisecond (RFC 9562 §6.2, method 1)

export function uuidv7(nowMs: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let ms = Math.max(nowMs, lastMs);
  if (ms === lastMs) {
    counter++;
    if (counter > 0xfff) {
      ms = lastMs + 1; // counter exhausted: borrow the next millisecond rather than repeat
      counter = (bytes[6]! & 0x07) << 8; // reseed low, leaving room to count up
    }
  } else {
    counter = ((bytes[6]! & 0x07) << 8) | bytes[7]!;
  }
  lastMs = ms;

  bytes[0] = Math.floor(ms / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ms / 2 ** 32) & 0xff;
  bytes[2] = (ms >>> 24) & 0xff;
  bytes[3] = (ms >>> 16) & 0xff;
  bytes[4] = (ms >>> 8) & 0xff;
  bytes[5] = ms & 0xff;
  bytes[6] = 0x70 | ((counter >>> 8) & 0x0f);
  bytes[7] = counter & 0xff;
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  let s = '';
  for (let i = 0; i < 16; i++) {
    s += HEX[bytes[i]!];
    if (i === 3 || i === 5 || i === 7 || i === 9) s += '-';
  }
  return s;
}

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export function isUuidV7(value: unknown): value is string {
  return typeof value === 'string' && UUID_V7_RE.test(value);
}

/** Milliseconds since the epoch encoded in a UUID v7. */
export function uuidv7Timestamp(id: string): number {
  if (!isUuidV7(id)) throw new TypeError(`not a UUID v7: ${id}`);
  return parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}
